import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  cancelProposedChip,
  proposeAvailableChip,
} from "core-integration/src/store/chips/actions";
import {
  getActiveChipName,
  getAvailableChipNames,
  getPotentialChips,
  getProposedChipName,
} from "core-integration/src/store/chips/reducers";
import { IPotentialChip } from "core-integration/src/store/chips/types";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { IElementTypesBySquadPosition } from "core-integration/src/store/element-types/types";
import {
  getErrors,
  getFreeTransfersRemaining,
  getProposedElements,
  getToSpend,
  getTransferCosts,
} from "core-integration/src/store/squad/reducers";
import {
  IProposedElements,
  ISquadErrors,
} from "core-integration/src/store/squad/types";
import { integerToMoney } from "core-integration/src/utils/money";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import { ChipName, getChipName } from "../../utils/chips";
import Alert from "../Alert";
import Button from "../Button";
import Dialog from "../Dialog";
import DialogHeading from "../DialogHeading";
import DialogManager from "../DialogManager";
import { ScoreboardPanel } from "./ScoreboardPanel";

const TransferSectionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[4]};
  padding: ${({ theme }) => theme.space[2]};
  background-color: ${({ theme }) => theme.colors.white4};
  border-radius: 12px;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex-direction: row;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    flex-direction: column;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex-direction: row;
  }
`;

const TransferSectionDataObjectWrap = styled.div`
  flex: 1.1;
  display: flex;
  gap: ${({ theme }) => theme.space[2]};
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    gap: ${({ theme }) => theme.space[3]};
  }
`;

const TransferSectionActionWrap = styled.div`
  flex: 0.9;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]};
`;

const TransferSectionActionObjectWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ theme }) => theme.space[2]};
`;

const CostScoreboardUnit = styled.div`
  flex: 1 1 150px;
  display: flex;
  align-items: stretch;
`;

const SquadSelectionWrap = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[3]};
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex-direction: row;
  }
`;

const SquadSelectionObjectWrap = styled.div`
  flex: 1;
  display: flex;
  gap: ${({ theme }) => theme.space[2]};
`;

interface IBasicProps {
  currencyDivisor: number;
  elementTypesByPosition: IElementTypesBySquadPosition;
  isBudgetExceeded: boolean;
  isNeedElements: boolean;
  proposedElements: IProposedElements;
  toSpend: number;
}

const Basic: React.FC<IBasicProps> = ({
  currencyDivisor,
  elementTypesByPosition,
  proposedElements,
  toSpend,
}) => {
  const { t } = useTranslation();
  return (
    <>
      <SquadSelectionWrap>
        <SquadSelectionObjectWrap>
          <ScoreboardPanel
            heading={t("scoreboard.playersSelected", "Players Selected")}
            value={`${Object.keys(proposedElements).length} / ${
              Object.keys(elementTypesByPosition).length
            }`}
          />

          <ScoreboardPanel
            heading={t("scoreboard.moneyRemaining", "Money Remaining")}
            value={`${integerToMoney(toSpend, currencyDivisor)}`}
          />
        </SquadSelectionObjectWrap>
      </SquadSelectionWrap>
    </>
  );
};

interface IExplainChipDialogProps {
  chip: string;
  handleHide: () => void;
  transferCosts: number;
}

const ExplainChipDialog: React.FC<IExplainChipDialogProps> = ({
  chip,
  handleHide,
  transferCosts,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        <DialogHeading>
          {chip === "wildcard" && t("squad.playWildcardText", "Play Wildcard")}
          {chip === "uteam" && t("squad.playUnlimitText", "Play Loan Rangers")}
        </DialogHeading>
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <Alert type="info">
          {transferCosts ? (
            <>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText1",
                    "You are now proposing to play your wildcard. Unlimited transfers can be made this Round without points deduction.",
                  )}
                {chip === "uteam" &&
                  t(
                    "squad.unlimitText1",
                    "You are now proposing to play your Loan Rangers chip. Unlimited transfers with as many players you want from a specific team can be picked this Round without points deduction.",
                  )}
              </p>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText2",
                    "Your wildcard will not be activated until you confirm your transfers.",
                  )}
                {chip === "uteam" &&
                  t(
                    "squad.unlimitText2",
                    "Your Loan Rangers chip will not be activated until you confirm your transfers",
                  )}
              </p>
            </>
          ) : (
            <p>
              {chip === "wildcard" &&
                t(
                  "squad.wildcardText3",
                  "To activate your wildcard you must be making enough proposed transfers that points will be deducted.",
                )}
              {chip === "uteam" &&
                t(
                  "squad.unlimitText3",
                  "To activate your Loan Rangers chip you must be making enough proposed transfers that points will be deducted.",
                )}
            </p>
          )}
        </Alert>
      </Dialog.Body>
    </Dialog>
  );
};

interface ICostProps {
  activeChipName: string;
  availableChipNames: string[];
  currencyDivisor: number;
  freeTransfersRemaining: number;
  isBudgetExceeded: boolean;
  proposedChipName: string;
  toSpend: number;
  transferCosts: number;
  cancelChip: (chip: string) => void;
  proposeChip: (chip: string) => void;
  chips: IPotentialChip[];
}

const Cost: React.FC<ICostProps> = ({
  activeChipName,
  currencyDivisor,
  freeTransfersRemaining,
  proposedChipName,
  toSpend,
  transferCosts,
  cancelChip,
  proposeChip,
  chips,
}) => {
  const { t } = useTranslation();
  // Note: These chips may be undefined during R19 preparation due to chip availability gaps
  // (wildcard: R2-R18, R20-R34; uteam: R2-R18, R20-R34)
  const unlimitChip = chips.filter((c) => c.name === "uteam")[0];
  const wildCardChip = chips.filter((c) => c.name === "wildcard")[0];
  return (
    <>
      <TransferSectionWrap>
        <TransferSectionDataObjectWrap>
          <ScoreboardPanel
            heading={t("squad.freeTransfers", "Free Transfers")}
            value={
              activeChipName === "wildcard" || activeChipName === "uteam"
                ? t("squad.unlimited", "Unlimited")
                : `${freeTransfersRemaining}`
            }
          />

          <ScoreboardPanel
            heading={t("squad.cost", "Cost")}
            value={`${transferCosts} pts`}
          />

          <ScoreboardPanel
            heading={t("squad.moneyRemaining", "Money Remaining")}
            value={`${integerToMoney(toSpend, currencyDivisor)}`}
          />
        </TransferSectionDataObjectWrap>
        <TransferSectionActionWrap>
          <TransferSectionActionObjectWrap>
            {activeChipName ? (
              <CostScoreboardUnit>
                <ScoreboardPanel
                  heading={t("scoreboard.chip.active", "Active Chip")}
                  value={getChipName(activeChipName as ChipName, t)}
                />
              </CostScoreboardUnit>
            ) : wildCardChip?.status_for_entry === "played" ? ( // Optional chaining: chip unavailable during R19
              <CostScoreboardUnit>
                <ScoreboardPanel
                  heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
                  value={t("scoreboard.wildcard.played", "Played")}
                />
              </CostScoreboardUnit>
            ) : wildCardChip?.status_for_entry === "active" ? ( // Optional chaining: chip unavailable during R19
              <CostScoreboardUnit>
                <ScoreboardPanel
                  heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
                  value={t("scoreboard.wildcard.active", "Active")}
                />
              </CostScoreboardUnit>
            ) : proposedChipName === "wildcard" ? (
              <CostScoreboardUnit>
                <Button width={1} onClick={() => cancelChip("wildcard")}>
                  {t("squad.cancelWildcardText", "Cancel Wildcard")}
                </Button>
              </CostScoreboardUnit>
            ) : wildCardChip ? ( // Only show "Play Wildcard" when chip exists
              <DialogManager
                render={(showDialog, handleShow, handleHide) => (
                  <>
                    <CostScoreboardUnit>
                      <Button
                        variant="primary"
                        disabled={
                          proposedChipName === "uteam" ||
                          activeChipName === "uteam"
                        }
                        width={1}
                        onClick={handleShow}
                      >
                        {t("squad.playWildcardTest", "Play Wildcard")}
                      </Button>
                    </CostScoreboardUnit>
                    {showDialog && (
                      <ExplainChipDialog
                        chip="wildcard"
                        handleHide={() => {
                          handleHide();
                          if (transferCosts) {
                            proposeChip("wildcard");
                          }
                        }}
                        transferCosts={transferCosts}
                      />
                    )}
                  </>
                )}
              />
            ) : null}

            {activeChipName ? (
              <></>
            ) : unlimitChip?.status_for_entry === "played" ? (
              <CostScoreboardUnit>
                <ScoreboardPanel
                  heading={t("scoreboard.loanrangers", "Loan Rangers")}
                  value={t("scoreboard.uteam.played", "Played")}
                />
              </CostScoreboardUnit>
            ) : unlimitChip?.status_for_entry === "active" ? ( // Optional chaining: chip unavailable during R19
              <CostScoreboardUnit>
                <ScoreboardPanel
                  heading={t("scoreboard.loanrangers", "Loan Rangers")}
                  value={t("scoreboard.uteam.active", "Active")}
                />
              </CostScoreboardUnit>
            ) : proposedChipName === "uteam" ? (
              <CostScoreboardUnit>
                <Button width={1} onClick={() => cancelChip("uteam")}>
                  {t("squad.cancelUnlimitText", "Cancel Loan Rangers")}
                </Button>
              </CostScoreboardUnit>
            ) : unlimitChip ? ( // Only show "Play Loan Rangers" when chip exists
              <DialogManager
                render={(showDialog, handleShow, handleHide) => (
                  <>
                    <CostScoreboardUnit>
                      <Button
                        variant="primary"
                        disabled={
                          proposedChipName === "wildcard" ||
                          activeChipName === "wildcard"
                        }
                        width={1}
                        onClick={handleShow}
                      >
                        {t("squad.playUnlimitText", "Play Loan Rangers")}
                      </Button>
                    </CostScoreboardUnit>
                    {showDialog && (
                      <ExplainChipDialog
                        chip="uteam"
                        handleHide={() => {
                          handleHide();
                          if (transferCosts) {
                            proposeChip("uteam");
                          }
                        }}
                        transferCosts={transferCosts}
                      />
                    )}
                  </>
                )}
              />
            ) : null}
          </TransferSectionActionObjectWrap>
        </TransferSectionActionWrap>
      </TransferSectionWrap>
    </>
  );
};

interface IOwnProps {
  scoreboard: string;
}

interface IPropsFromState {
  activeChipName: string;
  availableChipNames: string[];
  currencyDivisor: number;
  elementTypesByPosition: IElementTypesBySquadPosition | null;
  errors: ISquadErrors;
  freeTransfersRemaining: number;
  proposedChipName: string;
  proposedElements: IProposedElements;
  toSpend: number;
  transferCosts: number;
  chips: IPotentialChip[];
}

interface IPropsFromDispatch {
  cancelChip: (chip: string) => void;
  proposeChip: (chip: string) => void;
}

type Props = IOwnProps & IPropsFromState & IPropsFromDispatch;

class Scoreboard extends React.Component<Props> {
  public render() {
    const {
      elementTypesByPosition,
      errors,
      proposedElements,
      scoreboard,
      ...rest
    } = this.props;
    if (!elementTypesByPosition) {
      return null;
    }
    const isNeedElements = Boolean(errors.needElements);
    const isBudgetExceeded = Boolean(errors.budgetExceeded);
    switch (scoreboard) {
      case "basic":
        return (
          <Basic
            {...rest}
            elementTypesByPosition={elementTypesByPosition}
            isNeedElements={isNeedElements}
            isBudgetExceeded={isBudgetExceeded}
            proposedElements={proposedElements}
          />
        );
      case "cost":
        return <Cost {...rest} isBudgetExceeded={isBudgetExceeded} />;
      default:
        return null;
    }
  }
}

const mapStateToProps = (state: RootState): IPropsFromState => ({
  activeChipName: getActiveChipName(state),
  availableChipNames: getAvailableChipNames(state),
  currencyDivisor: 10,
  elementTypesByPosition: getElementTypesBySquadPosition(state),
  errors: getErrors(state),
  freeTransfersRemaining: getFreeTransfersRemaining(state),
  proposedChipName: getProposedChipName(state),
  proposedElements: getProposedElements(state),
  toSpend: getToSpend(state),
  transferCosts: getTransferCosts(state),
  chips: getPotentialChips(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch): IPropsFromDispatch => ({
  cancelChip: (chip: string) => dispatch(cancelProposedChip(chip)),
  proposeChip: (chip: string) => dispatch(proposeAvailableChip(chip)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Scoreboard);
